import React, { forwardRef, useState, useEffect, } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { overflowTextOffset } from '@polestar/component-warehouse-react/helpers/style';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import WithTooltip from '@polestar/component-warehouse-react/components/with-tooltip/WithTooltip';
import { useWidthCallback } from '@polestar/component-warehouse-react/hooks/useDimensions';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import { TableContext, useTable, useTableColumnIndex } from './TableContext';
import { mutableValue } from '@polestar/component-warehouse-react/hooks/useMutableValue';
import MediaQueries from '@polestar/component-warehouse-react/tokens/media-queries/MediaQueries';
import { visuallyHiddenStyles } from '@polestar/component-warehouse-react/components/hidden-accessibility-element/HiddenAccessibilityElement';
// TODO: we can split this up in ./TableBody.tsx, ./TableHead.tsx, etc...
const _Table = styled.table(({ collapseRows = false, zebraStripes = false }) => css(css `
      position: relative;
    `, collapseRows &&
    css `
        & tr {
            ${responsiveStyles({
        flexDirection: {
            mobile: 'column',
            desktop: 'unset',
        },
        display: {
            mobile: 'flex',
            desktop: 'table-row',
        },
    })};
        }

        & td {
            ${responsiveStyles({
        paddingBlockStart: {
            mobile: Spacings.medium,
        },
    })};
        }

        & td:last-of-type {
            ${responsiveStyles({
        paddingBlockEnd: {
            mobile: Spacings.large,
        },
    })};
        }

        & td:first-of-type {
            &:after {
                display: none;
            }

            ${MediaQueries.desktop} {
                &:after {
                    display: ${!zebraStripes ? 'block' : 'none'};
                }
            }


            ${responsiveStyles({
        paddingBlockStart: {
            mobile: Spacings.large,
        },
        paddingBlockEnd: {
            mobile: Spacings.none,
        },
    })};
        }

    ,
    `));
const Table = forwardRef(({ internal, stickyColumns, zebraStripes, stickyOffsetHeight, stickyHeader, ...props }, ref) => {
    const contextValue = {
        internal,
        zebraStripes,
        columnSizes: [],
        stickyColumns,
        stickyOffsetHeight,
        stickyHeader,
        hasThead: mutableValue(false),
    };
    return (React.createElement(TableContext.Provider, { value: contextValue },
        React.createElement(_Table, { ref: ref, ...props, internal: internal, zebraStripes: zebraStripes })));
});
export default Table;
const _TableBody = styled.tbody(({ theme, hasThead = false, zebraStripes = false }) => css(!zebraStripes &&
    !hasThead &&
    css `
          ${bottomBorder({ color: theme.table.lighterBorderColor, blockPosition: 'start' })}
        `));
export const TableBody = forwardRef((props, ref) => {
    const { hasThead, zebraStripes } = useTable();
    return React.createElement(_TableBody, { ref: ref, ...props, hasThead: hasThead.get(), zebraStripes: zebraStripes });
});
const _TableHead = styled.thead(({ theme }) => css(css `
    color: ${theme.table.headFontColor};
  `));
export const TableHead = forwardRef((props, ref) => {
    const { hasThead } = useTable();
    hasThead.set(true);
    return React.createElement(_TableHead, { ref: ref, ...props });
});
const _TableRow = styled.tr(({ theme, internal, zebraStripes = false }) => css(internal &&
    zebraStripes &&
    css `
        &:nth-of-type(2n) {
          background: ${theme.table.zebraStripe};

          td[data-sticky='true'] {
            background: ${theme.table.zebraStripe};
          }
        }
      `));
export const TableRow = forwardRef((props, ref) => {
    const { zebraStripes } = useTable();
    return React.createElement(_TableRow, { ref: ref, ...props, zebraStripes: zebraStripes });
});
const _TableHeader = styled.th(({ theme, internal, sticky, extraSpacing = false, isLastStickyColumn = false, hasThead = false, zebraStripes = false, stickyHeightOffset, stickyHeader, }) => css(css `
        text-align: start;
        color: ${theme.table.headFontColor};
        padding: ${Spacings.large} ${Spacings.medium} ${Spacings.large} 0;
        background: ${theme.table.backgroundColor};
      `, hasThead &&
    css `
          ${bottomBorder({ color: theme.table.borderColor, blockPosition: 'end' })}
        `, !hasThead &&
    !zebraStripes &&
    css `
          ${bottomBorder({ color: theme.table.lighterBorderColor, blockPosition: 'end' })}
        `, sticky != -1 &&
    css `
          position: sticky;
          /* this is dynamic to set columns next to each other */
          inset-inline-start: ${sticky}px;
          /* Patch the transparent border issue (scrolling text could be seen underneath) */

          &::before {
            position: absolute;
            inset-block-start: ${createCustomSpacing(1 / 16)};
            inset-inline-start: -${createCustomSpacing(3 / 16)};
            content: '';
            width: ${createCustomSpacing(3 / 16)};
            height: calc(100% - ${createCustomSpacing(1 / 8)});
            background: inherit;
          }

          z-index: 2;
        `, isLastStickyColumn &&
    css `
          &::before {
            position: absolute;
            inset-block-start: 0;
            inset-inline-end: 0;
            content: '';
            width: ${createCustomSpacing(1 / 16)};
            height: 100%;
            margin-inline-start: calc(100% + ${createCustomSpacing(2 / 16)});
            background: ${theme.table.borderColor};
          }
        `, internal &&
    zebraStripes &&
    css `
          padding-inline-start: ${Spacings.medium};
          padding-inline-end: ${Spacings.medium};
        `, sticky !== -1 &&
    css `
          padding-inline-start: ${Spacings.xSmall};
          padding-inline-end: ${Spacings.medium};
        `, extraSpacing &&
    css `
          /* TODO: why important */
          padding-inline-start: ${Spacings.medium} !important;
        `, stickyHeader &&
    css `
          position: sticky;
          inset-block-start: 0;
          z-index: 2;
        `, stickyHeightOffset > 0 &&
    css `
          inset-block-start: ${stickyHeightOffset}px;
        `));
export const TableHeader = forwardRef((props, ref) => {
    const [headerRef, headerWidth] = useWidthCallback('offsetWidth');
    const [columnRef, index] = useTableColumnIndex();
    const { hasThead, zebraStripes, stickyHeader, stickyOffsetHeight } = useTable();
    const combinedRef = useCombinedRef(ref, headerRef, columnRef);
    const [stickyHeightOffset, setStickyHeightOffset] = useState(typeof stickyOffsetHeight === 'number'
        ? stickyOffsetHeight
        : !!stickyOffsetHeight && stickyOffsetHeight.get());
    // provide index + column size for rendering stickyness of cells
    const { setColumnData, stickyColumns, columnSizes } = useTable();
    if (index !== -1) {
        setColumnData(index, headerWidth);
    }
    let stickyValue = -1;
    // TODO: use memo for performance optimisation later
    if (stickyColumns === null || stickyColumns === void 0 ? void 0 : stickyColumns.includes(index)) {
        // column is sticky
        const add = columnSizes.slice(0, index);
        stickyValue =
            // first column is selected which means inset-inline-start:0
            index === 0
                ? 0
                : // another index is selected
                    add.length > 0
                        ? add === null || add === void 0 ? void 0 : add.reduce((a, b) => a + b)
                        : -1;
    }
    // find the next column to add spacing for the initial right border issue
    const extraSpacing = (stickyColumns === null || stickyColumns === void 0 ? void 0 : stickyColumns.includes(index - 1)) || false;
    const isLastStickyColumn = (stickyColumns === null || stickyColumns === void 0 ? void 0 : stickyColumns.slice(-1)[0]) === index;
    useEffect(() => {
        if (typeof stickyOffsetHeight === 'number') {
            setStickyHeightOffset(stickyOffsetHeight);
        }
    }, [stickyOffsetHeight, stickyHeader]);
    if (stickyOffsetHeight && typeof stickyOffsetHeight !== 'number') {
        // MotionValue detected
        stickyOffsetHeight.on('change', (value) => {
            setStickyHeightOffset(value);
        });
    }
    return (React.createElement(_TableHeader, { ref: combinedRef, ...props, sticky: stickyValue, hasThead: hasThead.get(), extraSpacing: extraSpacing, isLastStickyColumn: isLastStickyColumn, zebraStripes: zebraStripes, stickyHeightOffset: stickyHeightOffset, stickyHeader: stickyHeader }));
});
const _TableCell = styled.td(({ theme, internal, zebraStripes = false, sticky, extraSpacing = false, isLastStickyColumn = false, }) => css(sticky != -1 &&
    css `
          position: sticky;
          /* this is dynamic to set columns next to each other */
          inset-inline-start: ${sticky}px;
          background: ${theme.table.backgroundColor};
          z-index: 1;
          /* Patch the transparent border issue (scrolling text could be seen underneath) */

          &::before {
            position: absolute;
            inset-block-start: ${createCustomSpacing(1 / 16)};
            inset-inline-start: -${createCustomSpacing(3 / 16)};
            content: '';
            width: ${createCustomSpacing(3 / 16)};
            height: calc(100% - ${createCustomSpacing(1 / 8)});
            background: inherit;
          }
        `, isLastStickyColumn &&
    // move vertical border to the end of the cell
    css `
          &::before {
            position: absolute;
            inset-block-start: 0;
            inset-inline-end: 0;
            content: '';
            width: ${createCustomSpacing(1 / 16)};
            height: 100%;
            margin-inline-start: calc(100% + ${createCustomSpacing(2 / 16)});
            background: ${theme.table.borderColor};
          }
        `, internal &&
    zebraStripes &&
    css `
          padding-inline-start: ${Spacings.medium};
          padding-inline-end: ${Spacings.medium};
          ${responsiveStyles({
        padding: {
            mobile: `${Spacings.large} ${Spacings.medium} ${Spacings.large} ${Spacings.medium}`,
            desktop: `${Spacings.large} ${Spacings.medium} ${Spacings.large}  ${Spacings.medium}`,
        },
    })};
        `, !zebraStripes &&
    css `
          ${responsiveStyles({
        padding: {
            mobile: `${Spacings.large} ${Spacings.medium} ${Spacings.large} ${Spacings.none}`,
            desktop: `${Spacings.large} ${Spacings.medium} ${Spacings.large} 0`,
        },
    })};
          ${bottomBorder({
        color: theme.table.lighterBorderColor,
        blockPosition: 'end',
        sticky: sticky != -1,
    })};
        `, sticky != -1 &&
    css `
          /* TODO: why important => responsiveStyles keeps getting precedence, why? */
          padding-inline-start: ${Spacings.xSmall} !important;
          padding-inline-end: ${Spacings.medium} !important;
        `, extraSpacing &&
    css `
          /* TODO: why important */
          padding-inline-start: ${Spacings.medium} !important;
        `));
export const TableCell = forwardRef(({ children, singleLine, ...props }, ref) => {
    const [cellRef, cellWidth] = useWidthCallback('offsetWidth');
    const [columnRef, index] = useTableColumnIndex();
    const combinedRef = useCombinedRef(ref, cellRef, columnRef);
    const { setColumnData, stickyColumns, columnSizes, zebraStripes } = useTable();
    if (index !== -1) {
        setColumnData(index, cellWidth);
    }
    let stickyValue = -1;
    // TODO: use memo for performance optimisation later
    if (stickyColumns === null || stickyColumns === void 0 ? void 0 : stickyColumns.includes(index)) {
        // column is sticky
        const add = columnSizes.slice(0, index);
        stickyValue =
            // first column is selected which means inset-inline-start:0
            index === 0
                ? 0
                : // another index is selected
                    add.length > 0
                        ? add === null || add === void 0 ? void 0 : add.reduce((a, b) => a + b)
                        : -1;
    }
    // find the next column to add spacing for the initial right border issue
    const extraSpacing = (stickyColumns === null || stickyColumns === void 0 ? void 0 : stickyColumns.includes(index - 1)) || false;
    const isLastStickyColumn = (stickyColumns === null || stickyColumns === void 0 ? void 0 : stickyColumns.slice(-1)[0]) === index;
    if (singleLine) {
        return (React.createElement(_TableCell, { ref: combinedRef, ...props, sticky: stickyValue, extraSpacing: extraSpacing, isLastStickyColumn: isLastStickyColumn, "data-sticky": stickyValue > -1, zebraStripes: zebraStripes },
            React.createElement(WithTooltip, { label: children },
                React.createElement(_TableCellLongText, { ref: ref, ...props }, children))));
    }
    return (React.createElement(_TableCell, { ref: combinedRef, ...props, sticky: stickyValue, extraSpacing: extraSpacing, isLastStickyColumn: isLastStickyColumn, "data-sticky": stickyValue > -1, zebraStripes: zebraStripes }, children));
});
const _TableCaption = styled.caption `
  ${visuallyHiddenStyles}
`;
/**
 * `TableCaption` specifies the caption (or title) of a table, providing the table an accessible description.
 * The caption is visually hidden to provide screen readers with a description without compromising visual design
 *  @link [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/caption)
 */
export const TableCaption = forwardRef(({ ...props }, ref) => {
    return React.createElement(_TableCaption, { ref: ref, ...props });
});
const _TableCellLongText = styled.div `
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  ${overflowTextOffset};
`;
const bottomBorder = ({ color, blockPosition, sticky = false, }) => css(css `
        &:after {
            position: absolute;
            inset-block-${blockPosition}: 0;
            inset-inline-start: 0;
            width: 100%;
            content: '';
            background: ${color};
            height: ${createCustomSpacing(1 / 16)};
        }
    `, !sticky &&
    css `
        position: relative;
      `);
